import axios, { type AxiosInstance } from "axios";
import { getAuth } from "firebase/auth";
import { useFirebase } from "igfd-commons";

import React, {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
} from "react";

import { config } from "../environment/config";
import { useRestaurant } from "../hooks/useRestaurant";

const createAxiosClient = () =>
  axios.create({
    baseURL: config.apiBaseUrl,
    headers: {
      "Content-Type": "application/json",
    },
  });

export type AxiosInstances = {
  clientWithAuth: AxiosInstance;
  clientWithoutAuth: AxiosInstance;
};

// Inital empty context
const createAxiosInstances = (): AxiosInstances => {
  // interceptors are added by the wrapper providers below
  // the providers are added by the respective layout components
  const clientWithAuth = createAxiosClient();
  const clientWithoutAuth = createAxiosClient();
  return {
    clientWithAuth,
    clientWithoutAuth,
  };
};

// same function as above, but exported for storybook.
export const axiosInstancesForStorybook = createAxiosInstances;

export const AxiosClientContext = createContext<AxiosInstances>(
  createAxiosInstances()
);

/**
 * Provider that adds the auth token to the request.
 * This is used to authenticate all requests.
 *
 * This is added by the AuthenticatedLayout
 */
export const AxiosAuthTokenProvider = ({ children }: PropsWithChildren) => {
  const { clientWithAuth } = useContext(AxiosClientContext);
  const firebase = useFirebase();
  const auth = getAuth(firebase.firebaseApp);

  useEffect(() => {
    const authInterceptorId = clientWithAuth.interceptors.request.use(
      async (config) => {
        const token = await auth.currentUser?.getIdToken();
        if (!token) {
          throw new Error("No token found");
        }
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      }
    );
    return () => {
      // FIXME ensure that we eject only when the restaurantUuid changes.
      clientWithAuth.interceptors.request.eject(authInterceptorId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.currentUser?.uid]);

  return <>{children}</>;
};

/**
 * Once the context of api calls are meant to happen in the context of a given
 * restaurant, this provider will add conxtual information to the request to allow
 * for access authorized endpoints.
 *
 * Auth firebase auth token has already been added.
 *
 * This is added by the RestaurantManagementLayout
 */
export const AxiosRestaurantAuthZProvider = ({
  children,
}: PropsWithChildren) => {
  const { clientWithAuth } = useContext(AxiosClientContext);
  const restaurant = useRestaurant();

  useEffect(() => {
    const authInterceptorId = clientWithAuth.interceptors.request.use(
      async (config) => {
        config.headers["x-restaurant-uuid"] = restaurant.uuid;
        return config;
      }
    );
    return () => {
      // FIXME ensure that we eject only when the restaurantUuid changes.
      clientWithAuth.interceptors.request.eject(authInterceptorId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant.uuid]);

  return <>{children}</>;
};

/**
 * Provider that doesn't add the auth token to the request.
 *
 * This is used by the storybook
 */
export const NoAuthAxiosClientProvider = ({ children }: PropsWithChildren) => {
  const { clientWithoutAuth } = useContext(AxiosClientContext);
  return (
    <AxiosClientContext.Provider
      value={{
        // storybook doesn't have access to firebase auth. Both clients will return not authorized axios client
        clientWithAuth: clientWithoutAuth,
        clientWithoutAuth,
      }}
    >
      {children}
    </AxiosClientContext.Provider>
  );
};

/**
 * Helper hook to get the correct axios client.
 */
export const useAxiosClient = ({ withAuth }: { withAuth: boolean }) => {
  const { clientWithAuth, clientWithoutAuth } = useContext(AxiosClientContext);
  if (withAuth) {
    return clientWithAuth;
  }
  return clientWithoutAuth;
};
